/**
 * React hook for accessing the current organization's plan entitlements.
 *
 * Reads `plan_id` from `orgBilling` (via OrgProvider) and resolves it to
 * a feature set. Effective organization overrides are read through Supabase RLS. No Stripe API calls.
 *
 * @module lib/useEntitlements
 */

import { useEffect, useMemo, useState } from 'react';
import { getSupabase } from './supabase';
import { captureException } from './telemetry/posthog/frontend';
import { useOrg } from './OrgProvider';
import {
  resolvePlanTier,
  hasFeature,
  getPlanLimits,
  getMinimumPlan,
  type PlanTier,
  type FeatureKey,
  type PlanLimits,
} from './entitlements';

/** Return type for the useEntitlements hook. */
export interface UseEntitlementsReturn {
  /** Resolved plan tier (free/pro/business). */
  tier: PlanTier;
  /** Check if a specific feature is available on the current plan. */
  can: (feature: FeatureKey) => boolean;
  /** Numeric limits for the current plan. */
  limits: PlanLimits;
  /** Whether the org context is still loading. */
  loading: boolean;
  /** Returns the minimum plan display name for a feature, or null if available on all plans. */
  minimumPlan: (feature: FeatureKey) => string | null;
}

/**
 * Returns the current org's plan tier, feature access checks, and plan limits.
 * Defaults to free tier when org/billing data is unavailable or when the
 * stored plan_id is not recognized (the resolver captures the mismatch to
 * PostHog rather than throwing during render).
 */
export function useEntitlements(): UseEntitlementsReturn {
  const { orgBilling, isLoading, activeOrg, billingRefreshKey } = useOrg();
  const orgId = activeOrg?.id;
  const [overrides, setOverrides] = useState<{ orgId: string; rows: Array<{ feature_key: string; enabled: boolean; expires_at: string | null }> } | null>(null);
  const [expiryRevision, setExpiryRevision] = useState(0);
  useEffect(() => {
    if (!orgId) return undefined;
    let cancelled = false;
    let expiryTimer: ReturnType<typeof setTimeout> | undefined;
    /** Reconcile current grants on billing changes, focus, and override expiry. */
    async function refreshOverrides() {
      try {
        const { data, error } = await getSupabase().from('org_feature_entitlements')
          .select('feature_key, enabled, expires_at').eq('organization_id', orgId!);
        if (error) throw error;
        if (cancelled) return;
        const rows = Array.isArray(data) ? data.filter(row => typeof row.feature_key === 'string' && typeof row.enabled === 'boolean') : [];
        setOverrides({ orgId: orgId!, rows });
        const expiries = [...rows.map(row => row.expires_at ? Date.parse(row.expires_at) : NaN), orgBilling?.subscription_status === 'trialing' && orgBilling.trial_ends_at ? Date.parse(orgBilling.trial_ends_at) : NaN].filter(at => at > Date.now());
        clearTimeout(expiryTimer);
        if (expiries.length) expiryTimer = setTimeout(() => setExpiryRevision(value => value + 1), Math.min(2147483647, Math.max(1, Math.min(...expiries) - Date.now())));
      } catch (error) {
        if (!cancelled) captureException(error instanceof Error ? error : new Error(String(error)), 'useEntitlements.overrides', { orgId });
      }
    }
    void refreshOverrides();
    window.addEventListener('focus', refreshOverrides);
    return () => { cancelled = true; clearTimeout(expiryTimer); window.removeEventListener('focus', refreshOverrides); };
  }, [orgId, orgBilling?.plan_id, orgBilling?.subscription_status, orgBilling?.trial_ends_at, billingRefreshKey, expiryRevision]);

  const tier = useMemo(
    () => orgBilling?.subscription_status === 'trialing' && orgBilling.trial_ends_at && Date.parse(orgBilling.trial_ends_at) <= Date.now()
      ? 'free' : resolvePlanTier(orgBilling?.plan_id),
    [orgBilling?.plan_id, orgBilling?.subscription_status, orgBilling?.trial_ends_at, expiryRevision],
  );

  const limits = useMemo(() => getPlanLimits(tier), [tier]);

  const can = useMemo(
    () => (feature: FeatureKey) => {
      const override = overrides && overrides.orgId === orgId ? overrides.rows.find(row => row.feature_key === feature && (!row.expires_at || Date.parse(row.expires_at) > Date.now())) : undefined;
      return override ? override.enabled : hasFeature(tier, feature);
    },
    [tier, overrides, orgId],
  );

  const minimumPlanFn = useMemo(
    () => (feature: FeatureKey) => getMinimumPlan(feature),
    [],
  );

  return {
    tier,
    can,
    limits,
    loading: isLoading,
    minimumPlan: minimumPlanFn,
  };
}
